import { type ReactNode, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, useLocation } from 'react-router-dom';
import { MainErrorFallback } from '@/app/components/errors/main-error.tsx';
import { VwgsLogo } from '@/app/components/ui/vwgs-logo.tsx';
import {
  AvatarActionArea,
  MainHeader,
  LanguageSelector
} from '@ki-components/frontend-common';
import { AvatarActionContent } from '@/app/components/ui/AvatarActionContent.tsx';
import {
  GroupuiAvatar,
  GroupuiLoadingSpinner,
  GroupuiText
} from '@group-ui/group-ui-react';
import { useAuth } from 'react-oidc-context';
import { GetLanguages } from '@/locales/locales-config.ts';
import { useTranslation } from 'react-i18next';
import { defaultLanguage } from '@/locales/i18n.ts';
import { AuthWatcher } from '@/app/hooks/authWatcher.tsx';
import { NavigationFooter } from '@/app/components/ui/NavigationFooter.tsx';
import { LOCATION } from '@/app/consts/location.ts';

/**
 * Provides the base layout for the whole application.
 * This layout consists of a header, side nav and a container for the content.
 * @returns {ReactNode} Returns a React element which renders a layout for the whole application.
 */
export function BaseLayout(): ReactNode {
  const auth = useAuth();
  const [isReady, setReady] = useState(true);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const languagesList = GetLanguages();

  const currentLanguage = i18n.language;

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || defaultLanguage;
    void i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const onChangeLanguage = async (langCode: string) => {
    setReady(false);
    await i18n.changeLanguage(langCode).then(() => {
      localStorage.setItem('i18nextLng', langCode);
      setReady(true);
    });
  };

  return (
    <>
      <MainHeader
        logo={<VwgsLogo className='h-7 select-none drag-none' />}
        actionArea={
          <div className='inline-flex justify-end'>
            <div className='inline-flex pr-300'>
              <LanguageSelector
                currentLanguage={currentLanguage}
                languagesList={languagesList}
                onChangeLanguage={onChangeLanguage}
              />
              {auth.isAuthenticated && (
                <AvatarActionArea
                  actionPopOver={
                    <GroupuiAvatar
                      size='m'
                      className='select-none drag-none'
                      inverted
                    />
                  }
                  actionAreaContent={<AvatarActionContent />}
                />
              )}
            </div>
          </div>
        }
        showActionArea={true}
        headLine={'Compliance Pilot'}
      />
      <div
        className={`w-full xl:h-[calc(100vh-122px)] ${currentLanguage === 'de-DE' ? 'md:h-[calc(100vh-140px)]' : 'md:h-[calc(100vh-122px)]'} sm:h-[calc(100vh-140px)] transition-all overflow-hidden`}
      >
        <Suspense
          fallback={
            <div className='flex size-full items-center justify-center'>
              <GroupuiLoadingSpinner displayed />
            </div>
          }
        >
          <ErrorBoundary fallback={<MainErrorFallback />}>
            {isReady && (
              <div className='flex flex-col h-full'>
                {location.pathname !== LOCATION.LOGIN &&
                  auth.isAuthenticated && <AuthWatcher />}
                <Outlet />
              </div>
            )}
          </ErrorBoundary>
        </Suspense>
      </div>
      <div className='sticky bottom-0 shadow-xl'>
        <GroupuiText
          className='text-center bg-[#EAF4F7] text-text-weaker p-1'
          size='caption'
        >
          {t('disclaimer')}
        </GroupuiText>
        <NavigationFooter />
      </div>
    </>
  );
}
